import { Injectable } from '@angular/core';

export type AppearanceMode = 'light' | 'dark' | 'system';

export const APPEARANCE_STORAGE_KEY = 'appearance';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private mode: AppearanceMode = 'dark';
  private media?: MediaQueryList;

  init(): void {
    const stored = this.normalize(localStorage.getItem(APPEARANCE_STORAGE_KEY));
    if (stored) {
      this.apply(stored, false);
      return;
    }

    try {
      const current = JSON.parse(localStorage.getItem('current') || '{}');
      const fromUser = this.normalize(current?.appearance);
      if (fromUser) {
        this.apply(fromUser, true);
        return;
      }
    } catch {
      // ignore broken storage
    }

    this.apply('dark', false);
  }

  get appearance(): AppearanceMode {
    return this.mode;
  }

  normalize(value?: string | null): AppearanceMode | null {
    if (value === 'light' || value === 'dark' || value === 'system') {
      return value;
    }
    return null;
  }

  apply(mode: AppearanceMode, persist = true): void {
    this.mode = mode;
    if (persist) {
      localStorage.setItem(APPEARANCE_STORAGE_KEY, mode);
    }
    this.paint();
    this.watchSystem(mode === 'system');
  }

  private watchSystem(enabled: boolean): void {
    if (!enabled) {
      this.media?.removeEventListener('change', this.onSystemChange);
      this.media = undefined;
      return;
    }
    if (this.media) {
      return;
    }
    this.media = window.matchMedia('(prefers-color-scheme: dark)');
    this.media.addEventListener('change', this.onSystemChange);
  }

  private readonly onSystemChange = (): void => {
    if (this.mode === 'system') {
      this.paint();
    }
  };

  private paint(): void {
    // Light screens are not ready yet. Claro, and Sistema when the OS is
    // light, stay visually dark. The chosen mode is still stored.
    const resolved = 'dark';
    document.documentElement.dataset['theme'] = resolved;
    document.documentElement.dataset['appearance'] = this.mode;
    document.documentElement.style.colorScheme = resolved;
  }
}
